const autoInstructionSentence =
  /Please (?:select|rank) (at least \d+ and no more than \d+ options?\.|at least \d+ options?\.|no more than \d+ options?\.|exactly \d+ options?\.)/;

export function buildCheckboxSelectionInstructionText(
  minSelectedChoices: number,
  maxSelectedChoices: number,
): string | null {
  const min = Math.max(0, minSelectedChoices ?? 0);
  const max = Math.max(0, maxSelectedChoices ?? 0);
  const hasMin = min >= 1;
  const hasMax = max >= 1;

  if (!hasMin && !hasMax) {
    return null;
  }

  if (hasMin && hasMax) {
    if (min === max) {
      return `Please select exactly ${min} option${min === 1 ? "" : "s"}.`;
    }

    return `Please select at least ${min} and no more than ${max} option${max === 1 ? "" : "s"}.`;
  }

  if (hasMin) {
    return `Please select at least ${min} option${min === 1 ? "" : "s"}.`;
  }

  return `Please select no more than ${max} option${max === 1 ? "" : "s"}.`;
}

/** Ranked Choice only has maxSelectedChoices (no min). */
export function buildRankingSelectionInstructionText(
  maxSelectedChoices: number,
): string | null {
  const max = Math.max(0, maxSelectedChoices ?? 0);
  if (max < 1) {
    return null;
  }

  return `Please rank no more than ${max} option${max === 1 ? "" : "s"}.`;
}

/** SurveyJS may store description as a string or as a locale map ({ default: "..." }). */
function descriptionSourceText(description: unknown): string {
  if (description == null) {
    return "";
  }

  if (typeof description === "string") {
    return description;
  }

  if (typeof description === "object" && "default" in description) {
    const defaultText = (description as { default?: unknown }).default;
    return typeof defaultText === "string" ? defaultText : "";
  }

  return "";
}

function plainDescriptionText(description: unknown): string {
  return descriptionSourceText(description)
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Author text with any auto min/max instruction sentences removed. */
function authorDescriptionText(description: unknown): string {
  return plainDescriptionText(description)
    .replace(new RegExp(autoInstructionSentence.source, "g"), " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Keep the auto-instruction where it already is (text before/after stays put).
 * First insert appends it after any existing author description.
 */
function descriptionWithInstruction(
  description: unknown,
  instruction: string,
): string {
  const plain = plainDescriptionText(description);
  if (!plain) {
    return instruction;
  }

  const match = plain.match(autoInstructionSentence);
  if (match && match.index != null) {
    const before = plain.slice(0, match.index).trim();
    const after = plain.slice(match.index + match[0].length).trim();
    return [before, instruction, after]
      .filter((part) => part.length > 0)
      .join(" ");
  }

  return `${plain} ${instruction}`;
}

export function isAutoSelectionInstruction(description: unknown): boolean {
  const text = plainDescriptionText(description);
  return text.length > 0 && authorDescriptionText(description).length === 0;
}

function ensureDescriptionVisible(target: {
  descriptionLocation?: string;
}): void {
  if (target.descriptionLocation === "hidden") {
    target.descriptionLocation = "underTitle";
  }
}

function supportsSelectionLimitInstruction(type: string | undefined): boolean {
  return type === "checkbox" || type === "ranking";
}

function selectionInstructionForElement(
  type: string | undefined,
  min: number,
  max: number,
): string | null {
  if (type === "ranking") {
    return buildRankingSelectionInstructionText(max);
  }

  return buildCheckboxSelectionInstructionText(min, max);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function syncCheckboxSelectionDescription(question: any): void {
  const type = question.getType?.();
  if (!supportsSelectionLimitInstruction(type)) {
    return;
  }

  // Ranking only exposes maxSelectedChoices; min stays 0 / hidden.
  const instruction = selectionInstructionForElement(
    type,
    question.minSelectedChoices ?? 0,
    question.maxSelectedChoices ?? 0,
  );

  if (instruction) {
    const next = descriptionWithInstruction(question.description, instruction);
    if (
      plainDescriptionText(question.description) !== plainDescriptionText(next)
    ) {
      question.description = next;
    }

    ensureDescriptionVisible(question);
  } else {
    const authorText = authorDescriptionText(question.description);
    const plain = plainDescriptionText(question.description);
    if (plain === authorText) {
      return;
    }

    question.description = authorText;
  }

  question.resetDescriptionVisibility?.();
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function syncAllCheckboxSelectionDescriptions(survey: any): void {
  survey.getAllQuestions?.().forEach(syncCheckboxSelectionDescription);
}
